/**
 * Shared run-progress mapping for both add-ins.
 *
 * The aira-rag-backend graph emits raw per-phase events
 * (GET /v1/drafts/:id/events). Both Outlook (live long-poll) and the Gmail
 * BFF (bounded poll-on-click) turn those into a user-facing step list. Keeping
 * the phase→label map and the reducer here means the two surfaces can never
 * drift, and neither shows fabricated steps.
 */
export const PROGRESS_GROUP_STATUSES = [
    'pending',
    'running',
    'completed',
    'failed',
    'skipped',
];
export function isProgressGroupStatus(value) {
    return PROGRESS_GROUP_STATUSES.some((status) => status === value);
}
/**
 * Backend graph phase → friendly, user-facing label. Phases not listed are
 * intentionally hidden from the UI (still present in raw events for
 * debugging) so the panel stays readable.
 */
export const PHASE_LABELS = {
    loadTenantConfig: 'Loading workspace settings',
    piiAnonymization: 'Protecting personal data',
    decomposeQuery: 'Breaking down the request',
    classifyIntent: 'Understanding the request',
    resolveOrderByEmail: 'Matching the customer order',
    loadRouting: 'Selecting the right playbook',
    resolveDimensions: 'Resolving context',
    searchQueryEnrichment: 'Refining the search',
    retrieveHybrid: 'Searching merchant knowledge',
    reranker: 'Ranking the best sources',
    executionAgent: 'Checking availability & order details',
    aggregateContext: 'Gathering everything together',
    generateDraft: 'Drafting the reply',
    guardrail: 'Reviewing for accuracy',
    persistRun: 'Finalising',
};
/** Canonical, ordered list of user-visible plan phases (PHASE_LABELS order). */
export const PHASE_ORDER = Object.keys(PHASE_LABELS);
/**
 * Reduce a (possibly partial, possibly unordered) list of raw run events into
 * an ordered, deduped, labelled step list. Latest status per phase wins;
 * first-seen order is preserved. Stateless on purpose — pass the full event
 * list you currently have (works for GAS's stateless per-click polling and
 * for the task pane's accumulated long-poll alike).
 */
export function summarizeEvents(events) {
    const order = [];
    const statusByPhase = new Map();
    const durationByPhase = new Map();
    for (const ev of [...events].sort((a, b) => a.id - b.id)) {
        if (!PHASE_LABELS[ev.phase])
            continue;
        if (!statusByPhase.has(ev.phase))
            order.push(ev.phase);
        statusByPhase.set(ev.phase, ev.status);
        if (ev.status === 'completed')
            durationByPhase.set(ev.phase, ev.duration_ms ?? null);
    }
    return order.map((p) => ({
        phase: p,
        label: PHASE_LABELS[p],
        status: statusByPhase.get(p) ?? 'started',
        durationMs: durationByPhase.get(p) ?? null,
    }));
}
/**
 * Reduce raw events into the FULL canonical plan: every PHASE_ORDER phase is
 * always present, in order, so the UI can show the whole plan as a checklist
 * (done ✓ / active ● / pending ○) from the very first render. Phases with no
 * event yet are 'pending'. This is what both add-ins render so they tally
 * exactly — Outlook fills it in live (1s poll), Gmail advances it per tap.
 */
export function planSteps(events) {
    const statusByPhase = new Map();
    const durationByPhase = new Map();
    for (const ev of [...events].sort((a, b) => a.id - b.id)) {
        if (!PHASE_LABELS[ev.phase])
            continue;
        statusByPhase.set(ev.phase, ev.status);
        if (ev.status === 'completed')
            durationByPhase.set(ev.phase, ev.duration_ms ?? null);
    }
    return PHASE_ORDER.map((p) => ({
        phase: p,
        label: PHASE_LABELS[p],
        status: statusByPhase.get(p) ?? 'pending',
        durationMs: durationByPhase.get(p) ?? null,
    }));
}
//# sourceMappingURL=progress.js.map